import { LIVE_WORKFLOW_LIMITS, planAtLeast, WORKFLOWS_MIN_PLAN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, ErrorState, Icon, StatCard } from "@sagot/ui";
import { useState } from "react";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceDetails } from "../../../session/workspace-context";
import { PageHeader } from "../shared/page-header";
import { CreateWorkflowDialog, type CreateMode } from "./create-dialog";
import { splitByOrigin } from "./defaults/defaults-model";
import { DefaultsSection } from "./defaults/defaults-section";
import { UpgradeSheet } from "./defaults/upgrade-prompts";
import { OwnWorkflows } from "./own-workflows";
import { useWorkflowsWithDefaults } from "./use-workflows";

const W = "engage.workflows";

/** `/w/:ws/workflows`: the default workflows every workspace is given, then the workspace's own grouped by trigger, in run order, with 30-day counts. */
export function WorkflowsPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const canManage = permissions.can("workflows.manage") && !permissions.readOnly;
  const canPublish = permissions.can("workflows.publish") && !permissions.readOnly;
  // The list is open to every plan (the defaults are everyone's). A plan that cannot be read yet never locks anything.
  const details = useWorkspaceDetails();
  const plan = details.data?.plan;
  const planBlocked = plan !== undefined && !planAtLeast(plan, WORKFLOWS_MIN_PLAN);
  const list = useWorkflowsWithDefaults(!details.isPending);
  const [creating, setCreating] = useState<CreateMode | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  const { defaults, own } = splitByOrigin(list.data ?? []);
  const open = (mode: CreateMode) => { if (planBlocked) setUpgrading(true); else setCreating(mode); };
  // On a plan without workflows of its own the actions stay, marked with a lock: they explain the plan instead of failing.
  const newButton = canManage ? (
    <Button onClick={() => { open("trigger"); }}>
      <Icon name={planBlocked ? "lock" : "plus"} />
      {t(`${W}.newWorkflow`)}
    </Button>
  ) : null;
  const actions = newButton ? (
    <>
      <Button variant="secondary" onClick={() => { open("template"); }}>
        <Icon name={planBlocked ? "lock" : "layers"} />
        {t(`${W}.templates.open`)}
      </Button>
      {newButton}
    </>
  ) : null;
  const liveCount = own.filter((r) => r.state === "live").length;
  const liveLimit = plan === undefined ? undefined : LIVE_WORKFLOW_LIMITS[plan as keyof typeof LIVE_WORKFLOW_LIMITS];
  const totals = (list.data ?? []).reduce((sum, row) => ({ sent: sum.sent + row.stats.sent, completed: sum.completed + row.stats.completed, failed: sum.failed + row.stats.failed }), { sent: 0, completed: 0, failed: 0 });
  const liveNote = liveLimit === undefined ? undefined : t(`${W}.kpis.liveOf`, { count: liveCount, limit: liveLimit });
  const footnote = defaults.length > 0 ? [liveNote, t(`${W}.defaults.kpiDefaults`, { count: defaults.length })].filter(Boolean).join(" · ") : liveNote;
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader title={t(`${W}.title`)} subtitle={t(`${W}.subtitle`)} actions={actions} />
      {list.data ? (
        <section className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-label={t(`${W}.defaults.kpiLabel`)}>
          <StatCard label={t(`${W}.kpis.live`)} value={String(liveCount)} icon="workflow" footnote={footnote} />
          <StatCard label={t(`${W}.kpis.runs`)} value={String(totals.sent)} icon="activity" footnote={t(`${W}.defaults.kpiRunsHint`)} />
          <StatCard label={t(`${W}.kpis.completed`)} value={String(totals.completed)} icon="check-circle" />
          <StatCard label={t(`${W}.kpis.failed`)} value={String(totals.failed)} icon="alert-triangle" />
        </section>
      ) : null}
      {list.isError ? <ErrorState title={t(`${W}.loadFailed`)} onRetry={() => void list.refetch()} retryLabel={t("common.retry")} /> : (
        <DefaultsSection defaults={list.data ? defaults : undefined} canManage={canManage} canPublish={canPublish} duplicateLocked={planBlocked} />
      )}
      {list.data ? <OwnWorkflows workflows={own} canPublish={canPublish} planBlocked={planBlocked} newButton={newButton} /> : null}
      {creating ? <CreateWorkflowDialog initialMode={creating} onClose={() => { setCreating(null); }} /> : null}
      {upgrading ? <UpgradeSheet onClose={() => { setUpgrading(false); }} /> : null}
    </div>
  );
}
